import React, { useState } from "react";
import {
  Form,
  Input,
  Button,
  Card,
  Image,
  Space,
  Alert,
  Typography,
} from "antd";
import { SafetyCertificateOutlined, ArrowLeftOutlined } from "@ant-design/icons";
import { useDispatch } from "react-redux";
import { useNavigate } from "react-router-dom";

import { setCredentials } from "./store/authSlice";
import { useMutations } from "./hooks/useApiMutation";
import { homePathForRole } from "./components/ProtectedRoute";

const { Text } = Typography;
const brandMarkSrc =
  (process.env.PUBLIC_URL || "") + "/brand/kubit-mark.png";

function Login() {
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const { mutate, loading } = useMutations();

  // Set when the password was accepted but the account has an authenticator. It holds the
  // short-lived hand-off token — deliberately in component state, never in localStorage:
  // it is a half-finished login, not a session, and it must die with the tab.
  const [challenge, setChallenge] = useState(null);
  const [codeForm] = Form.useForm();

  // Both steps end the same way, so the landing logic lives in one place.
  const completeSignIn = (tokens) => {
    dispatch(setCredentials(tokens));
    if (tokens?.mustChangePassword) {
      navigate("/change-password", { replace: true });
      return;
    }
    // An account the platform requires an authenticator from can only reach its own
    // profile until it enrols — send it straight there instead of to a wall of 403s.
    if (tokens?.twoFactorSetupRequired) {
      navigate(tokens?.role === "Client" ? "/portal/profile" : "/profile", { replace: true });
      return;
    }
    navigate(homePathForRole(tokens?.role), { replace: true });
  };

  const onFinish = async (values) => {
    await mutate("login", {
      endpoint: "/auth/login",
      method: "post",
      payload: values,
      onSuccess: (tokens) => {
        if (tokens?.requiresTwoFactor) {
          setChallenge({
            token: tokens.twoFactorToken,
            username: tokens.username,
          });
          return;
        }
        completeSignIn(tokens);
      },
    });
  };

  const onVerifyCode = async (values) => {
    await mutate("loginTwoFactor", {
      endpoint: "/auth/login/2fa",
      method: "post",
      payload: {
        twoFactorToken: challenge.token,
        code: values.code,
      },
      onSuccess: completeSignIn,
    });
  };

  const restart = () => {
    setChallenge(null);
    codeForm.resetFields();
  };

  return (
    <div className="login-layout">
      <div className="login-bg" />
      <div className="login-content">
        <div className="login-card fade-up">
          <Card styles={{ body: { padding: 32 } }}>
            <Space
              direction="vertical"
              align="center"
              style={{ width: "100%" }}
              size={12}
            >
              <Image
                src={brandMarkSrc}
                alt="Kubit"
                preview={false}
                width={64}
                height={64}
              />
              <div style={{ textAlign: "center" }}>
                <div className="login-brand">AI Integration</div>
                <div className="login-subtitle">
                  {challenge
                    ? "Two-step verification"
                    : "Sign in to your Kubit workspace"}
                </div>
              </div>
            </Space>

            {challenge ? (
              <Form
                form={codeForm}
                name="loginTwoFactor"
                onFinish={onVerifyCode}
                layout="vertical"
                style={{ marginTop: 24 }}
                size="large"
              >
                <Alert
                  type="info"
                  showIcon
                  icon={<SafetyCertificateOutlined />}
                  style={{ marginBottom: 16 }}
                  message={`Enter the code for ${challenge.username}`}
                  description="Open your authenticator app and type the 6-digit code it shows. You can also use one of your recovery codes."
                />

                <Form.Item
                  name="code"
                  label="Verification code"
                  rules={[{ required: true, message: "Ju lutem shkruani kodin!" }]}
                >
                  <Input
                    placeholder="123456"
                    autoFocus
                    autoComplete="one-time-code"
                    inputMode="numeric"
                    style={{ letterSpacing: 4, textAlign: "center" }}
                  />
                </Form.Item>

                <Form.Item style={{ marginBottom: 8, marginTop: 8 }}>
                  <Button
                    type="primary"
                    htmlType="submit"
                    block
                    loading={loading?.loginTwoFactor}
                    className="login-btn"
                  >
                    Verify
                  </Button>
                </Form.Item>

                <Button type="link" block icon={<ArrowLeftOutlined />} onClick={restart}>
                  Back to sign in
                </Button>
              </Form>
            ) : (
              <Form
                name="login"
                onFinish={onFinish}
                layout="vertical"
                style={{ marginTop: 24 }}
                size="large"
              >
                <Form.Item
                  name="username"
                  label="Username"
                  rules={[
                    { required: true, message: "Ju lutem shkruani perdoruesin!" },
                  ]}
                >
                  <Input placeholder="Enter your username" autoFocus />
                </Form.Item>

                <Form.Item
                  name="password"
                  label="Password"
                  rules={[
                    { required: true, message: "Ju lutem shkruani fjalekalimin!" },
                  ]}
                >
                  <Input.Password placeholder="Enter your password" />
                </Form.Item>

                <Form.Item style={{ marginBottom: 0, marginTop: 8 }}>
                  <Button
                    type="primary"
                    htmlType="submit"
                    block
                    loading={loading?.login}
                    className="login-btn"
                  >
                    Sign In
                  </Button>
                </Form.Item>
              </Form>
            )}

            {!challenge && (
              <div style={{ textAlign: "center", marginTop: 16 }}>
                <Text type="secondary" style={{ fontSize: 12 }}>
                  Protected by two-step verification
                </Text>
              </div>
            )}
          </Card>
        </div>
      </div>
    </div>
  );
}

export default Login;
